import { useEffect, useMemo, useRef } from 'react';
import {
  Prompt,
  PromptUpdateRequest,
  PromptCreateRequest,
  PromptOwnerType,
  PromptTypeType,
  PromptAccountScopeType,
  SkillApplicabilityType,
} from '../../../types/backend';
import { usePromptService } from '../services/promptService';
import { useEntityManagement, EntityCrudService } from '../../../shared/hooks/useEntityManagement';
import { getContentValidationError } from '../utils/promptUtils';

export interface PromptFormData {
  owner: PromptOwnerType;
  type: PromptTypeType;
  name: string;
  title: string;
  description: string;
  accountScope: PromptAccountScopeType;
  agentType: SkillApplicabilityType;
  tags: string[];
  content: string;
}

const EMPTY_FORM: PromptFormData = {
  owner: 'customer',
  type: 'playbook',
  name: '',
  title: '',
  description: '',
  accountScope: 'customer',
  agentType: 'both',
  tags: [],
  content: ''
};

export const getNameError = (name: string, type?: PromptTypeType): string | null => {
  if (!name) return null;
  if (type === 'skill') {
    if (name.length > 64) return "Skill name must be 64 characters or less";
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(name)) {
      return "Lowercase letters, numbers, and hyphens only. Must not start/end with hyphen.";
    }
    if (name.includes('--')) return "No consecutive hyphens allowed";
    return null;
  }
  if (name.length < 3) return "Must be at least 3 characters";
  if (name.length > 100) return "Must be 100 characters or less";
  if (!/^([0-9a-zA-Z][_-]?){1,100}$/.test(name)) {
    return "Only alphanumeric, underscores, and hyphens allowed (no spaces)";
  }
  return null;
};

export const getDescriptionError = (description: string, type?: PromptTypeType): string | null => {
  if (type === 'skill') {
    if (!description || description.length < 100) return "Skill description must be at least 100 characters";
    if (description.length > 1024) return "Must be 1024 characters or less";
    return null;
  }
  if (!description) return null;
  if (description.length > 500) return "Must be 500 characters or less";
  return null;
};

export function usePromptManagement(customerId: string | undefined, isOpen: boolean) {
  const promptService = usePromptService();
  const entitiesRef = useRef<Prompt[]>([]);

  const crudService = useMemo((): EntityCrudService<Prompt, PromptCreateRequest & { owner: PromptOwnerType }, PromptUpdateRequest> => ({
    getAll: async () => {
      const prompts = await promptService.getAllPrompts(customerId || '');
      return prompts;
    },
    create: (data: PromptCreateRequest & { owner: PromptOwnerType }) => {
      const { owner, ...request } = data;
      return promptService.createPrompt(customerId || '', request, owner);
    },
    update: (id, dto) => {
      const prompt = entitiesRef.current.find(p => p.id === id);
      const owner = prompt?.owner || 'customer';
      return promptService.updatePrompt(id, dto, owner, customerId);
    },
    delete: (id) => {
      const prompt = entitiesRef.current.find(p => p.id === id);
      const owner = prompt?.owner || 'customer';
      return promptService.deletePrompt(id, owner, customerId);
    }
  }), [promptService, customerId]);

  const entityManagement = useEntityManagement<Prompt, PromptFormData>({
    service: crudService,
    queryKey: ['prompt-studio', 'prompts', customerId ?? null],
    isOpen,
    emptyForm: EMPTY_FORM,
    getEntityId: (prompt) => prompt.id,
    entityToForm: (prompt) => ({
      owner: prompt.owner,
      type: (prompt.type as PromptTypeType) || 'playbook',
      name: prompt.name,
      title: prompt.title,
      description: prompt.description,
      accountScope: prompt.accountScope || 'customer',
      agentType: (prompt.agentType as SkillApplicabilityType) || 'both',
      tags: [...(prompt.tags || [])],
      content: prompt.content || ''
    }),
    formToCreateDto: (form) => ({
      owner: form.owner,
      name: form.name,
      title: form.title,
      description: form.description,
      content: form.content,
      type: form.type,
      accountScope: form.type === 'playbook' ? form.accountScope : undefined,
      agentType: form.type === 'skill' ? form.agentType : undefined,
      tags: form.tags,
    }),
    formToUpdateDto: (form) => ({
      name: form.name,
      title: form.title,
      description: form.description,
      content: form.content,
      accountScope: form.type === 'playbook' ? form.accountScope : undefined,
      agentType: form.type === 'skill' ? form.agentType : undefined,
      tags: form.tags,
    }),
    validateForm: (form) => ({
      owner: (form.owner === 'customer' || form.owner === 'global') ? null : 'Invalid owner',
      name: !form.name || !form.name.trim() ? 'Name is required' : getNameError(form.name, form.type),
      title: !form.title || form.title.trim().length < 3
        ? 'Title must be at least 3 characters'
        : form.title.length > 100 ? 'Title must be 100 characters or less' : null,
      content: !form.content || !form.content.trim() ? 'Content is required' : getContentValidationError(form.content),
      description: getDescriptionError(form.description, form.type),
    }),
    hasFormChanges: (form, prompt, isNew) => {
      if (isNew) return true;
      if (!prompt) return false;
      const tagsChanged = JSON.stringify([...(form.tags)].sort()) !== JSON.stringify([...(prompt.tags || [])].sort());
      return form.name !== prompt.name ||
        form.title !== prompt.title ||
        form.description !== prompt.description ||
        form.content !== prompt.content ||
        form.accountScope !== (prompt.accountScope || 'customer') ||
        tagsChanged;
    }
  });

  // Keep the id→owner lookup (used by update/delete) in sync with the cached list,
  // since getAll may be served from cache without re-running.
  useEffect(() => {
    entitiesRef.current = entityManagement.entities;
  }, [entityManagement.entities]);

  return { promptService, entityManagement };
}
